import {
  Block,
  Section,
  GeneratedDocumentData,
  PageData,
} from "../types/documentTypes";
import { OutputData } from "@editorjs/editorjs";

// Debounce function
export const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Enhanced function to convert content strings to blocks
export function convertContentToBlocks(content: string): Block[] {
  if (!content) return [];

  // Split content into meaningful blocks
  const lines = content.split("\n");
  const blocks: Block[] = [];
  let currentBlock: any = null;
  let blockId = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Skip empty lines
    if (line === "") {
      if (currentBlock) {
        blocks.push(currentBlock);
        currentBlock = null;
      }
      continue;
    }

    // Check for headers (# Header)
    const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headerMatch) {
      if (currentBlock) {
        blocks.push(currentBlock);
      }

      currentBlock = {
        id: `block-${blockId++}-${Date.now()}`,
        type: "header",
        content: headerMatch[2],
        level: headerMatch[1].length,
      };

      blocks.push(currentBlock);
      currentBlock = null;
      continue;
    }

    // Check for list items
    if (
      line.startsWith("- ") ||
      line.startsWith("* ") ||
      line.match(/^\d+\.\s+/)
    ) {
      const listContent = line.replace(/^[- *]\s+|^\d+\.\s+/, "");

      // If we're not already building a list, start a new one
      if (!currentBlock || currentBlock.type !== "list") {
        if (currentBlock) {
          blocks.push(currentBlock);
        }

        currentBlock = {
          id: `block-${blockId++}-${Date.now()}`,
          type: "list",
          content: line,
          items: [listContent],
        };
      } else {
        // Add to existing list
        currentBlock.items = currentBlock.items || [];
        currentBlock.items.push(listContent);
        currentBlock.content += `\n${line}`;
      }
      continue;
    }

    // Check for image markdown ![alt](url)
    const imageMatch = line.match(/!\[(.*?)\]\((.*?)\)/);
    if (imageMatch) {
      console.log('🖼️ convertContentToBlocks found image:', {
        line,
        alt: imageMatch[1],
        src: imageMatch[2],
        srcLength: imageMatch[2]?.length,
        isFullUrl: imageMatch[2]?.startsWith('http'),
        isSupabaseUrl: imageMatch[2]?.includes('supabase.co')
      });
      
      if (currentBlock) {
        blocks.push(currentBlock);
      }

      currentBlock = {
        id: `block-${blockId++}-${Date.now()}`,
        type: "image",
        content: imageMatch[2], // The URL or base64 data
        caption: imageMatch[1] || "", // The alt text as caption
      };

      console.log('🖼️ convertContentToBlocks created image block:', currentBlock);

      blocks.push(currentBlock);
      currentBlock = null;
      continue;
    }

    // Check for blockquotes
    if (line.startsWith('>')) {
      if (currentBlock) {
        blocks.push(currentBlock);
      }
      currentBlock = {
        id: `block-${blockId++}-${Date.now()}`,
        type: 'blockquote',
        content: line.replace(/^>\s*/, ''),
      };
      blocks.push(currentBlock);
      currentBlock = null;
      continue;
    }

    // Regular paragraph text
    if (!currentBlock || currentBlock.type !== "text") {
      if (currentBlock) {
        blocks.push(currentBlock);
      }

      currentBlock = {
        id: `block-${blockId++}-${Date.now()}`,
        type: "text",
        content: line,
      };
    } else {
      // Append to existing paragraph
      currentBlock.content += `\n${line}`;
    }
  }

  // Add the last block if it exists
  if (currentBlock) {
    blocks.push(currentBlock);
  }

  return blocks;
}

// Helper function to convert blocks back to content
export function convertBlocksToContent(blocks: Block[]): string {
  return blocks
    .map((block) => {
      switch (block.type) {
        case "header":
          const prefix = "#".repeat(block.level || 1);
          return `${prefix} ${block.content}`;
        case "list":
          if (block.items && block.items.length > 0) {
            // Enhanced list processing to handle object items
            const processedItems = block.items
              .filter((item: any) => item !== null && item !== undefined)
              .map((item: any) => {
                if (typeof item === "string") {
                  return item;
                } else if (typeof item === "object" && item.content) {
                  return item.content;
                } else if (typeof item === "object" && item.text) {
                  return item.text;
                } else if (typeof item === "object") {
                  console.warn(
                    "List item is an object in convertBlocksToMarkdown:",
                    item
                  );
                  return JSON.stringify(item);
                } else {
                  return String(item);
                }
              })
              .map((item: string) => item.replace(/<[^>]*>/g, "").trim())
              .filter(
                (item: string) =>
                  item !== "" && !item.includes("[object Object]")
              );

            if (processedItems.length > 0) {
              return processedItems.map((item) => `- ${item}`).join("\n");
            }
          }
          return block.content;
        case "image":
          // For images, preserve the full content including base64 data
          if (block.content && block.content !== 'placeholder' && !block.content.includes('placeholder')) {
            // If it's already in markdown format, return as is
            if (
              block.content.startsWith("![") &&
              block.content.includes("](")
            ) {
              return block.content;
            }
            // If it's base64 data or URL, wrap it in markdown format
            const caption = block.caption || "Image";
            return `![${caption}](${block.content})`;
          } else {
            // 🚨 FIXED: Don't create dummy image placeholders - return empty string
            console.log("⚠️ Skipping empty/placeholder image block");
            return "";
          }
        default:
          return block.content;
      }
    })
    .join("\n\n");
}

// Helper function to process markdown formatting in text
const processMarkdownFormatting = (text: any): string => {
  // Ensure we have a string to work with
  if (typeof text !== "string") {
    if (text === null || text === undefined) {
      return "";
    }
    // Convert to string if it's not already
    text = String(text);
  }

  return (
    text
      // Bold text: **text** or __text__
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/__(.*?)__/g, "<strong>$1</strong>")
      // Italic text: *text* or _text_
      .replace(/\*(.*?)\*/g, "<em>$1</em>")
      .replace(/_(.*?)_/g, "<em>$1</em>")
      // Code: `code`
      .replace(
        /`(.*?)`/g,
        '<code style="background-color: #f1f5f9; padding: 0.125rem 0.25rem; border-radius: 0.25rem; font-family: monospace;">$1</code>'
      )
      // HTML img tags (preserve dimensions, add styling) - MUST come before markdown images!
      .replace(
        /<img\s+([^>]*?)>/g,
        (match, attrs) => {
          // Extract alt text for caption
          const altMatch = attrs.match(/alt\s*=\s*["']([^"']*)["']/i);
          const alt = altMatch ? altMatch[1] : '';
          
          // Enhance the img tag with consistent styling
          const enhancedAttrs = attrs.includes('style=') 
            ? attrs.replace(/style\s*=\s*["']([^"']*)["']/i, (m, existingStyle) => 
                `style="${existingStyle}; max-width: 100%; height: auto; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);"`)
            : `${attrs} style="max-width: 100%; height: auto; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);"`;
          
          const imgTag = `<img ${enhancedAttrs} onError="this.style.display='none'">`;
          const caption = alt ? `<div style="font-style: italic; color: #6b7280; margin-top: 0.5rem; font-size: 0.875rem;">${alt}</div>` : '';
          
          return `<div style="text-align: center; margin: 1.5rem 0;">${imgTag}${caption}</div>`;
        }
      )
      // Images: ![alt](url) - Standard markdown images
      .replace(
        /!\[([^\]]*)\]\(([^)]+)\)/g,
        (match, alt, src) => {
          console.log('🖼️ processMarkdownFormatting processing image:', { 
            alt, 
            src, 
            fullMatch: match,
            srcLength: src?.length,
            isFullUrl: src?.startsWith('http'),
            isSupabaseUrl: src?.includes('supabase.co')
          });
          return `<div style="text-align: center; margin: 1.5rem 0; padding: 0.5rem; border: 1px solid #e5e7eb; border-radius: 8px; background: #f9fafb;"><img src="${src}" alt="${alt}" style="max-width: 100%; height: auto; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); display: block;" onError="console.error('processMarkdownFormatting: Image failed to load:', '${src}'); this.style.display='none'; this.nextElementSibling.style.display='block';" onLoad="console.log('processMarkdownFormatting: Image loaded successfully:', '${src}');"><div style="display: none; text-align: center; padding: 1rem; color: #ef4444; font-style: italic; border: 1px dashed #fca5a5; border-radius: 4px; background: #fef2f2;">⚠️ Image failed to load: ${alt || 'Image'}</div><div style="font-style: italic; color: #6b7280; margin-top: 0.5rem; font-size: 0.875rem;">${alt}</div></div>`;
        }
      )
      // Links: [text](url)
      .replace(
        /\[([^\]]+)\]\(([^)]+)\)/g,
        '<a href="$2" style="color: #3b82f6; text-decoration: underline;">$1</a>'
      )
      // Convert newlines to HTML line breaks
      .replace(/\n/g, "<br>")
  );
};

// Enhanced function to render blocks as HTML for preview with full Editor.js support
export const renderBlocksToHTML = (
  blocks: any[],
  theme?: {
    headingColor?: string;
    subheadingColor?: string;
    textColor?: string;
    accentColor?: string;
    fontFamily?: string;
  }
): string => {
  // Default colors if theme is not provided
  const defaultTheme = {
    headingColor: "#1f2937",
    subheadingColor: "#374151",
    textColor: "#4b5563",
    accentColor: "#3b82f6",
    fontFamily: "Inter, system-ui, -apple-system, sans-serif",
  };

  const appliedTheme = { ...defaultTheme, ...theme };
  return blocks
    .map((block) => {
      switch (block.type) {
        case "header":
        case "heading":
          // Handle Editor.js header blocks with hierarchical color system
          const headerText = block.data?.text || block.content || "";
          const level = Math.min(
            Math.max(block.data?.level || block.level || 2, 1),
            6
          );
          const tag = `h${level}`;
          const fontSize =
            level === 1
              ? "2rem"
              : level === 2
              ? "1.5rem"
              : level === 3
              ? "1.25rem"
              : "1.125rem";

          // Apply hierarchical color system
          const headerColor =
            level === 1
              ? appliedTheme.headingColor // Primary color for H1
              : appliedTheme.subheadingColor; // Secondary color for H2, H3, etc.

          return `<${tag} style="margin: 0.75rem 0; font-weight: 600; font-size: ${fontSize}; line-height: 1.2; color: ${headerColor}; font-family: ${appliedTheme.fontFamily};">${headerText}</${tag}>`;

        case "paragraph":
        case "text":
          // Handle Editor.js paragraph blocks with font color
          const paragraphText = block.data?.text || block.content || "";
          const formattedText = processMarkdownFormatting(paragraphText);
          return `<p style="margin: 0.5rem 0; line-height: 1.6; color: ${appliedTheme.textColor}; font-family: ${appliedTheme.fontFamily};">${formattedText}</p>`;

        case "list":
          // Handle Editor.js list blocks with full support for unordered, ordered, and checklist
          const listData = block.data || {};
          const listStyle = listData.style || block.style || "unordered";
          const listItems = listData.items || block.items || [];

          if (listItems.length === 0) return "";

          // Process list items based on style
          if (listStyle === "checklist") {
            // Handle checklist items with checkboxes
            const checklistItems = listItems
              .filter((item: any) => item !== null && item !== undefined)
              .map((item: any) => {
                let itemText = "";
                let isChecked = false;

                if (typeof item === "string") {
                  itemText = item;
                } else if (typeof item === "object") {
                  itemText = item.text || item.content || "";
                  isChecked = item.checked === true;
                }

                // Clean up text
                itemText = itemText.replace(/<[^>]*>/g, "").trim();
                if (!itemText || itemText.includes("[object Object]"))
                  return "";

                const formattedItem = processMarkdownFormatting(itemText);
                const checkboxStyle = isChecked
                  ? `background-color: ${appliedTheme.accentColor}; border-color: ${appliedTheme.accentColor};`
                  : "background-color: white; border-color: #d1d5db;";
                const checkmark = isChecked ? "✓" : "";

                return `
                  <li style="display: flex; align-items: flex-start; margin: 0.5rem 0; list-style: none; font-family: ${
                    appliedTheme.fontFamily
                  };">
                    <div style="width: 16px; height: 16px; border: 2px solid; border-radius: 3px; margin-right: 0.5rem; margin-top: 0.125rem; display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: bold; color: white; ${checkboxStyle}">
                      ${checkmark}
                    </div>
                    <span style="flex: 1; color: ${
                      isChecked ? "#6b7280" : appliedTheme.textColor
                    }; ${
                  isChecked ? "text-decoration: line-through;" : ""
                }">${formattedItem}</span>
                  </li>
                `;
              })
              .filter((item: string) => item !== "")
              .join("");

            if (checklistItems) {
              return `<ul style="margin: 0.5rem 0; padding-left: 0; line-height: 1.5;">${checklistItems}</ul>`;
            }
            return "";
          } else {
            // Handle regular unordered and ordered lists
            const processedItems = listItems
              .filter((item: any) => item !== null && item !== undefined)
              .map((item: any) => {
                let itemText = "";
                if (typeof item === "string") {
                  itemText = item;
                } else if (typeof item === "object" && item.text) {
                  itemText = item.text;
                } else if (typeof item === "object" && item.content) {
                  itemText = item.content;
                } else if (typeof item === "object") {
                  console.warn("List item is an object in preview:", item);
                  itemText = JSON.stringify(item);
                } else {
                  itemText = String(item);
                }

                // Clean up text
                itemText = itemText.replace(/<[^>]*>/g, "").trim();
                return itemText;
              })
              .filter(
                (item: string) =>
                  item !== "" && !item.includes("[object Object]")
              );

            if (processedItems.length === 0) return "";

            const listItemsHTML = processedItems
              .map((item: string) => {
                const formattedItem = processMarkdownFormatting(item);
                return `<li style="margin: 0.25rem 0; color: ${appliedTheme.textColor}; font-family: ${appliedTheme.fontFamily};">${formattedItem}</li>`;
              })
              .join("");

            if (listStyle === "ordered") {
              return `<ol style="margin: 0.5rem 0; padding-left: 1.5rem; line-height: 1.5; list-style-type: decimal; color: ${appliedTheme.textColor}; font-family: ${appliedTheme.fontFamily};">${listItemsHTML}</ol>`;
            } else {
              return `<ul style="margin: 0.5rem 0; padding-left: 1.5rem; line-height: 1.5; list-style-type: disc; color: ${appliedTheme.textColor}; font-family: ${appliedTheme.fontFamily};">${listItemsHTML}</ul>`;
            }
          }

        case "image":
          // Handle Editor.js image blocks
          const imageData = block.data || {};
          const imageUrl =
            imageData.url || imageData.file?.url || block.content || "";
          const imageCaption = imageData.caption || block.caption || "";
          const imageAlt = imageCaption || "Image";

          console.log('🖼️ renderBlocksToHTML processing image block:', {
            block,
            imageData,
            imageUrl,
            imageCaption,
            hasContent: !!block.content,
            contentLength: block.content?.length,
            isFullUrl: imageUrl?.startsWith('http'),
            isSupabaseUrl: imageUrl?.includes('supabase.co')
          });

          if (imageUrl) {
            return `
              <div style="margin: 1rem 0; text-align: center; padding: 0.5rem; border: 1px solid #e5e7eb; border-radius: 8px; background: #f9fafb;">
                <img src="${imageUrl}" alt="${imageAlt}" style="max-width: 100%; height: auto; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); display: block;" onError="console.error('renderBlocksToHTML: Image failed to load:', '${imageUrl}'); this.style.display='none'; this.nextElementSibling.style.display='block';" onLoad="console.log('renderBlocksToHTML: Image loaded successfully:', '${imageUrl}');" />
                <div style="display: none; text-align: center; padding: 1rem; color: #ef4444; font-style: italic; border: 1px dashed #fca5a5; border-radius: 4px; background: #fef2f2;">⚠️ Image failed to load: ${imageAlt}</div>
                ${
                  imageCaption
                    ? `<p style="margin-top: 0.5rem; font-size: 0.875rem; color: #666; font-style: italic;">${imageCaption}</p>`
                    : ""
                }
              </div>
            `;
          }
          return `
            <div style="padding: 2rem; border: 2px dashed #ccc; text-align: center; color: #666; margin: 0.5rem 0; border-radius: 8px; background-color: #f9f9f9;">
              <p style="margin: 0; font-style: italic;">📷 Image placeholder</p>
            </div>
          `;

        case "threeColumns":
        case "columns":
          // Handle three-column layout blocks
          const columnsData = block.data || {};
          const columns = columnsData.columns || block.columns || [];

          if (columns.length > 0) {
            const columnsHTML = columns
              .map((column: any) => {
                const columnBlocks = column.blocks || [];
                const columnContent = renderBlocksToHTML(columnBlocks);
                return `
                  <div style="flex: 1; min-width: 0; padding: 0 0.5rem; box-sizing: border-box;">
                    ${
                      columnContent ||
                      '<p style="color: #999; font-style: italic; text-align: center; padding: 2rem;">Empty column</p>'
                    }
                  </div>
                `;
              })
              .join("");

            return `
              <div style="display: flex; gap: 1rem; margin: 1rem 0; min-height: 100px; border: 1px solid #e5e7eb; border-radius: 8px; padding: 1rem; background-color: #fafafa;">
                ${columnsHTML}
              </div>
            `;
          }
          return `
            <div style="padding: 1rem; border: 2px dashed #ccc; text-align: center; color: #666; margin: 0.5rem 0; border-radius: 8px; background-color: #f9f9f9;">
              <p style="margin: 0; font-style: italic;">📐 Empty column layout</p>
            </div>
          `;

        case "quote":
          // Handle Editor.js quote blocks
          const quoteData = block.data || {};
          const quoteText = quoteData.text || block.content || "";
          const quoteCaption = quoteData.caption || block.caption || "";

          return `
            <blockquote style="margin: 1rem 0; padding: 1rem; border-left: 4px solid #3b82f6; background-color: #f8fafc; font-style: italic;">
              <p style="margin: 0; font-size: 1.125rem; line-height: 1.6;">${processMarkdownFormatting(
                quoteText
              )}</p>
              ${
                quoteCaption
                  ? `<cite style="display: block; margin-top: 0.5rem; font-size: 0.875rem; color: #6b7280; font-style: normal;">— ${quoteCaption}</cite>`
                  : ""
              }
            </blockquote>
          `;

        case "code":
          // Handle Editor.js code blocks
          const codeData = block.data || {};
          const codeText = codeData.code || block.content || "";

          return `
            <pre style="margin: 1rem 0; padding: 1rem; background-color: #1f2937; color: #f9fafb; border-radius: 8px; overflow-x: auto; font-family: 'Courier New', monospace; font-size: 0.875rem; line-height: 1.5;"><code>${codeText}</code></pre>
          `;

        case "delimiter":
          // Handle Editor.js delimiter blocks
          return `
            <div style="margin: 2rem 0; text-align: center;">
              <div style="display: inline-block; font-size: 1.5rem; color: #d1d5db;">***</div>
            </div>
          `;

        default:
          // Handle unknown block types
          const defaultContent = block.data?.text || block.content || "";
          if (defaultContent) {
            const processedContent = processMarkdownFormatting(defaultContent);
            return `<div style="margin: 0.5rem 0; padding: 0.5rem; background-color: #f5f5f5; border-radius: 4px; border-left: 3px solid #ddd;">${processedContent}</div>`;
          }
          return "";
      }
    })
    .filter((html) => html !== "")
    .join("");
};

// Function to convert content to markdown
export function convertToMarkdown(documentData: GeneratedDocumentData): string {
  let markdown = `# ${documentData.title}\n\n`;

  if (documentData.client) {
    markdown += `**Client:** ${documentData.client}\n\n`;
  }

  // Add sections
  if (documentData.sections && documentData.sections.length > 0) {
    documentData.sections.forEach((section) => {
      markdown += `## ${section.title}\n\n`;

      // Process blocks in each section
      section.blocks.forEach((block) => {
        switch (block.type) {
          case "header":
            const headerLevel = block.level || 2;
            const prefix = "#".repeat(headerLevel + 1); // +1 because we already have a level-2 section header
            markdown += `${prefix} ${block.content}\n\n`;
            break;

          case "list":
            if (block.items && block.items.length > 0) {
              // Enhanced list processing to handle object items
              const processedItems = block.items
                .filter((item: any) => item !== null && item !== undefined)
                .map((item: any) => {
                  if (typeof item === "string") {
                    return item;
                  } else if (typeof item === "object" && item.content) {
                    return item.content;
                  } else if (typeof item === "object" && item.text) {
                    return item.text;
                  } else if (typeof item === "object") {
                    console.warn(
                      "List item is an object in convertSectionToMarkdown:",
                      item
                    );
                    return JSON.stringify(item);
                  } else {
                    return String(item);
                  }
                })
                .map((item: string) => item.replace(/<[^>]*>/g, "").trim())
                .filter(
                  (item: string) =>
                    item !== "" && !item.includes("[object Object]")
                );

              processedItems.forEach((item) => {
                markdown += `- ${item}\n`;
              });
              markdown += "\n";
            }
            break;

          case "image":
            const caption = block.caption ? ` "${block.caption}"` : "";
            // Include the full base64 image data in the markdown
            if (block.content.startsWith("data:image")) {
              markdown += `![Image${caption}](${block.content})\n\n`;
            } else {
              // Fallback for non-data URLs
              markdown += `![Image${caption}](${block.caption || "image"})\n\n`;
            }
            break;

          default: // text
            markdown += `${block.content}\n\n`;
        }
      });
    });
  }

  return markdown;
}

// Function to split sections into pages - improved to maintain better formatting
export function splitIntoPages(sections: Section[]): PageData[] {
  console.log('🚨 SPLITINTOPAGES ENTRY:', {
    sectionsCount: sections?.length || 0,
    sectionsReceived: sections?.map(s => ({
      id: s.id,
      title: s.title,
      contentLength: s.content?.length || 0,
      hasContent: !!(s.content && s.content.trim()),
      contentPreview: s.content ? s.content.substring(0, 50) + '...' : 'NO CONTENT'
    })) || []
  });

  if (!sections || sections.length === 0) {
    console.log('🚨 SPLITINTOPAGES: No sections provided, returning empty array');
    return [];
  }

  const pages: PageData[] = [];
  const maxWordsPerPage = 500; // Adjust this value based on your page layout
  const minSectionsPerPage = 1;
  const maxSectionsPerPage = 3; // Don't put too many sections on one page

  let currentPage: PageData = {
    id: `page-1`,
    content: [],
  };
  let currentWordCount = 0;
  let pageNumber = 1;

  for (let i = 0; i < sections.length; i++) {
    const section = sections[i];

    console.log(`🔍 SPLITINTOPAGES: Processing section ${i + 1}/${sections.length}:`, {
      sectionId: section.id,
      sectionTitle: section.title,
      contentLength: section.content?.length || 0,
      hasContent: !!(section.content && section.content.trim()),
      currentPageSections: currentPage.content.length,
      currentPageNumber: pageNumber
    });

    // Estimate word count for this section
    const sectionText = section.content || "";
    const sectionWordCount = sectionText.split(/\s+/).length;

    // Check if we should start a new page
    const shouldStartNewPage =
      currentPage.content.length >= maxSectionsPerPage || // Too many sections
      (currentPage.content.length >= minSectionsPerPage &&
        currentWordCount + sectionWordCount > maxWordsPerPage); // Too much content

    console.log(`🔍 SPLITINTOPAGES: Page decision for section ${section.id}:`, {
      shouldStartNewPage,
      currentPageSections: currentPage.content.length,
      maxSectionsPerPage,
      currentWordCount,
      sectionWordCount,
      maxWordsPerPage,
      willCreateNewPage: shouldStartNewPage && currentPage.content.length > 0
    });

    if (shouldStartNewPage && currentPage.content.length > 0) {
      // Finish current page
      console.log(`📄 SPLITINTOPAGES: Finishing page ${pageNumber} with ${currentPage.content.length} sections`);
      pages.push(currentPage);

      // Start new page
      pageNumber++;
      currentPage = {
        id: `page-${pageNumber}`,
        content: [],
      };
      currentWordCount = 0;
      console.log(`📄 SPLITINTOPAGES: Starting new page ${pageNumber}`);
    }

    // 🚨 CRITICAL FIX: ALWAYS add section to current page, regardless of content
    console.log(`✅ SPLITINTOPAGES: Adding section ${section.id} to page ${pageNumber}`);
    currentPage.content.push(section);
    currentWordCount += sectionWordCount;

    // 🚨 ENSURE section was actually added
    console.log(`✅ SPLITINTOPAGES: Section ${section.id} added. Current page now has ${currentPage.content.length} sections`);
  }

  // Add the last page if it has content
  if (currentPage.content.length > 0) {
    console.log(`📄 SPLITINTOPAGES: Adding final page ${pageNumber} with ${currentPage.content.length} sections`);
    pages.push(currentPage);
  } else {
    console.log(`⚠️ SPLITINTOPAGES: Final page ${pageNumber} is empty, not adding`);
  }

  // If no pages were created, create a default page with all content
  if (pages.length === 0) {
    pages.push({
      id: "page-1",
      content: sections,
    });
  }

  console.log(
    `📄 Created ${pages.length} pages from ${sections.length} sections`
  );

  // 🚨 DETAILED DEBUG: Track all sections through pagination
  console.log("🔍 SPLITINTOPAGES DEBUG:");
  console.log("Input sections:", sections.map(s => ({ id: s.id, title: s.title, contentLength: s.content?.length || 0 })));

  pages.forEach((page, index) => {
    console.log(`📄 Page ${index + 1}: ${page.content.length} sections`);
    console.log(`   Sections on page ${index + 1}:`, page.content.map(s => ({ id: s.id, title: s.title, contentLength: s.content?.length || 0 })));
  });

  // 🚨 CRITICAL: Check if any sections are missing
  const allSectionsInPages = pages.flatMap(page => page.content);
  const missingSections = sections.filter(section =>
    !allSectionsInPages.some(pageSection => pageSection.id === section.id)
  );

  if (missingSections.length > 0) {
    console.error("🚨 SECTIONS LOST IN SPLITINTOPAGES:", missingSections.map(s => ({ id: s.id, title: s.title })));

    // 🚨 EMERGENCY FIX: Add missing sections to the last page
    console.log("🚨 EMERGENCY: Adding missing sections to last page");
    if (pages.length === 0) {
      pages.push({
        id: 'emergency-page-1',
        content: []
      });
    }

    const lastPage = pages[pages.length - 1];
    missingSections.forEach(missingSection => {
      console.log(`🚨 EMERGENCY: Adding missing section ${missingSection.id} to last page`);
      lastPage.content.push(missingSection);
    });

    console.log("🚨 EMERGENCY FIX APPLIED: All sections now included");
  } else {
    console.log("✅ All sections preserved in splitIntoPages");
  }

  return pages;
}

// Helper function to convert file to base64
export const convertFileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

// Enhanced function to render Markdown to HTML with rich formatting support
export const renderMarkdownToHTML = (markdown: string): string => {
  if (!markdown || typeof markdown !== "string") {
    return "";
  }

  return (
    markdown
      // Headers
      .replace(
        /^### (.*$)/gm,
        '<h3 style="margin: 1rem 0 0.5rem 0; font-weight: 600; font-size: 1.25rem; color: #1f2937;">$1</h3>'
      )
      .replace(
        /^## (.*$)/gm,
        '<h2 style="margin: 1.25rem 0 0.75rem 0; font-weight: 600; font-size: 1.5rem; color: #1f2937;">$1</h2>'
      )
      .replace(
        /^# (.*$)/gm,
        '<h1 style="margin: 1.5rem 0 1rem 0; font-weight: 600; font-size: 2rem; color: #1f2937;">$1</h1>'
      )

      // Bold and Italic
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.*?)\*/g, "<em>$1</em>")
      .replace(/__(.*?)__/g, "<strong>$1</strong>")
      .replace(/_(.*?)_/g, "<em>$1</em>")

      // Code blocks
      .replace(
        /```([\s\S]*?)```/g,
        '<pre style="background-color: #f1f5f9; padding: 1rem; border-radius: 6px; overflow-x: auto; margin: 1rem 0;"><code style="font-family: Monaco, Menlo, monospace; font-size: 0.875rem;">$1</code></pre>'
      )

      // Inline code
      .replace(
        /`(.*?)`/g,
        '<code style="background-color: #f1f5f9; padding: 0.125rem 0.25rem; border-radius: 0.25rem; font-family: Monaco, Menlo, monospace; font-size: 0.875rem;">$1</code>'
      )

      // Blockquotes
      .replace(
        /^> (.*$)/gm,
        '<blockquote style="border-left: 4px solid #3b82f6; padding-left: 1rem; margin: 1rem 0; font-style: italic; color: #4b5563;">$1</blockquote>'
      )

      // Process lists properly
      .replace(
        /(\n|^)(\d+\.\s+.*(?:\n\d+\.\s+.*)*)/gm,
        (match, prefix, listContent) => {
          const items = listContent
            .split(/\n\d+\.\s+/)
            .filter((item) => item.trim() && !item.includes("[object Object]"));
          const listItems = items
            .map(
              (item) =>
                `<li style="margin: 0.25rem 0;">${item
                  .replace(/^\d+\.\s+/, "")
                  .replace(/\[object Object\]/g, "")
                  .trim()}</li>`
            )
            .filter(
              (item) => !item.includes('<li style="margin: 0.25rem 0;"></li>')
            )
            .join("");
          return `${prefix}<ol style="margin: 1rem 0; padding-left: 1.5rem;">${listItems}</ol>`;
        }
      )
      .replace(
        /(\n|^)([-*+]\s+.*(?:\n[-*+]\s+.*)*)/gm,
        (match, prefix, listContent) => {
          const items = listContent
            .split(/\n[-*+]\s+/)
            .filter((item) => item.trim() && !item.includes("[object Object]"));
          const listItems = items
            .map(
              (item) =>
                `<li style="margin: 0.25rem 0;">${item
                  .replace(/^[-*+]\s+/, "")
                  .replace(/\[object Object\]/g, "")
                  .trim()}</li>`
            )
            .filter(
              (item) => !item.includes('<li style="margin: 0.25rem 0;"></li>')
            )
            .join("");
          return `${prefix}<ul style="margin: 1rem 0; padding-left: 1.5rem; list-style-type: disc;">${listItems}</ul>`;
        }
      )

      // Images (must come before links)
      .replace(
        /!\[([^\]]*)\]\(([^)]+)\)/g,
        (match, alt, src) => {
          console.log('🖼️ renderMarkdownToHTML processing image:', { 
            alt, 
            src, 
            fullMatch: match,
            srcLength: src?.length,
            isFullUrl: src?.startsWith('http'),
            isSupabaseUrl: src?.includes('supabase.co')
          });
          return `<div style="text-align: center; margin: 1.5rem 0; padding: 0.5rem; border: 1px solid #e5e7eb; border-radius: 8px; background: #f9fafb;"><img src="${src}" alt="${alt}" style="max-width: 100%; height: auto; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); display: block;" onError="console.error('renderMarkdownToHTML: Image failed to load:', '${src}'); this.style.display='none'; this.nextElementSibling.style.display='block';" onLoad="console.log('renderMarkdownToHTML: Image loaded successfully:', '${src}');"><div style="display: none; text-align: center; padding: 1rem; color: #ef4444; font-style: italic; border: 1px dashed #fca5a5; border-radius: 4px; background: #fef2f2;">⚠️ Image failed to load: ${alt || 'Image'}</div><div style="font-style: italic; color: #6b7280; margin-top: 0.5rem; font-size: 0.875rem;">${alt}</div></div>`;
        }
      )

      // Links
      .replace(
        /\[([^\]]+)\]\(([^)]+)\)/g,
        '<a href="$2" style="color: #3b82f6; text-decoration: underline;">$1</a>'
      )

      // Horizontal rules
      .replace(
        /^---$/gm,
        '<hr style="margin: 2rem 0; border: none; border-top: 1px solid #e5e7eb;">'
      )

      // Three-column layouts
      .replace(
        /<div class="three-columns">([\s\S]*?)<\/div>/g,
        '<div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 1rem; margin: 1.5rem 0; padding: 1rem; border: 1px solid #e5e7eb; border-radius: 8px; background: #f9fafb;">$1</div>'
      )
      .replace(
        /<div class="column-(\d+)">([\s\S]*?)<\/div>/g,
        '<div style="padding: 0.75rem; border: 1px dashed #d1d5db; border-radius: 6px; background: white; min-height: 100px;">$2</div>'
      )

      // Paragraphs (convert double line breaks to paragraphs)
      .replace(
        /\n\n/g,
        '</p><p style="margin: 0.75rem 0; line-height: 1.6; color: #374151;">'
      )

      // Single line breaks
      .replace(/\n/g, "<br>")

      // Wrap in paragraph tags if not already wrapped
      .replace(
        /^(?!<[h1-6]|<blockquote|<pre|<ul|<ol|<li)(.+)/gm,
        '<p style="margin: 0.75rem 0; line-height: 1.6; color: #374151;">$1</p>'
      )

      // Clean up empty paragraphs
      .replace(/<p[^>]*><\/p>/g, "")

      // Wrap lists in proper ul/ol tags
      .replace(/(<li[^>]*>.*?<\/li>)/gs, (match) => {
        // Check if it's an ordered list (starts with number)
        if (
          match.includes("1.") ||
          match.includes("2.") ||
          match.includes("3.")
        ) {
          return `<ol style="margin: 1rem 0; padding-left: 1.5rem;">${match}</ol>`;
        } else {
          return `<ul style="margin: 1rem 0; padding-left: 1.5rem; list-style-type: disc;">${match}</ul>`;
        }
      })
  );
};

// Function to render Editor.js JSON directly to HTML
export const renderEditorJsonToHTML = (editorData: OutputData): string => {
  if (!editorData?.blocks) return "";

  return editorData.blocks
    .map((block: any) => {
      switch (block.type) {
        case "header":
          const level = Math.min(Math.max(block.data.level || 2, 1), 6);
          const tag = `h${level}`;
          const fontSize =
            level === 1
              ? "2rem"
              : level === 2
              ? "1.5rem"
              : level === 3
              ? "1.25rem"
              : "1.125rem";
          // Editor.js headers may contain HTML formatting
          const headerText = block.data.text || "";
          return `<${tag} style="margin: 0.75rem 0; font-weight: 600; font-size: ${fontSize}; line-height: 1.2; color: #1f2937;">${headerText}</${tag}>`;

        case "paragraph":
          // Editor.js paragraphs may contain HTML formatting (bold, italic, etc.)
          const paragraphText = block.data.text || "";
          return `<p style="margin: 0.5rem 0; line-height: 1.6; color: #374151;">${paragraphText}</p>`;

        case "list":
          const items = block.data.items || [];
          const listStyle = block.data.style === "ordered" ? "ol" : "ul";

          // Enhanced list processing to handle object items
          const processedItems = items
            .filter((item: any) => item !== null && item !== undefined)
            .map((item: any) => {
              if (typeof item === "string") {
                return item;
              } else if (typeof item === "object" && item.content) {
                return item.content;
              } else if (typeof item === "object" && item.text) {
                return item.text;
              } else if (typeof item === "object") {
                console.warn(
                  "List item is an object in renderEditorJsonToHTML:",
                  item
                );
                return JSON.stringify(item);
              } else {
                return String(item);
              }
            })
            .map((item: string) => item.replace(/<[^>]*>/g, "").trim())
            .filter(
              (item: string) => item !== "" && !item.includes("[object Object]")
            );

          if (processedItems.length === 0) return "";

          const listItems = processedItems
            .map(
              (item: string) =>
                `<li style="margin: 0.25rem 0; color: #374151;">${item}</li>`
            )
            .join("");
          return `<${listStyle} style="margin: 0.5rem 0; padding-left: 1.5rem;">${listItems}</${listStyle}>`;

        case "quote":
          const quoteText = block.data.text || "";
          const caption = block.data.caption || "";
          return `<blockquote style="margin: 1rem 0; padding: 1rem; border-left: 4px solid #3b82f6; background-color: #f8fafc; font-style: italic; color: #1f2937;">
            ${quoteText}
            ${
              caption
                ? `<cite style="display: block; margin-top: 0.5rem; font-size: 0.875rem; color: #6b7280; text-align: right;">— ${caption}</cite>`
                : ""
            }
          </blockquote>`;

        case "code":
          return `<pre style="margin: 1rem 0; padding: 1rem; background-color: #f1f5f9; border: 1px solid #e2e8f0; border-radius: 6px; overflow-x: auto;"><code style="font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace; font-size: 0.875rem; color: #1f2937;">${
            block.data.code || ""
          }</code></pre>`;

        case "delimiter":
          return `<hr style="margin: 2rem 0; border: none; border-top: 1px solid #e5e7eb;" />`;

        default:
          return `<p style="margin: 0.5rem 0; line-height: 1.6; color: #374151;">${
            block.data.text || block.data.content || ""
          }</p>`;
      }
    })
    .join("");
};

// Helper function to convert advanced blocks to regular blocks
export function convertAdvancedBlocksToBlocks(advancedBlocks: any[]): Block[] {
  return advancedBlocks.map((block, index) => ({
    id: block.id || `converted-${index}-${Date.now()}`,
    type: block.type === "heading" ? "header" : block.type,
    content:
      block.type === "text"
        ? block.content?.text
        : block.type === "heading"
        ? block.content?.text
        : block.type === "image"
        ? block.content?.src
        : block.content,
    level: block.type === "heading" ? block.content?.level : block.level,
    items: block.type === "list" ? block.content?.items : block.items,
    caption:
      block.type === "image"
        ? block.content?.caption || block.content?.alt
        : undefined,
  }));
}

// Helper function to extract plain text from blocks for search/preview
export function extractTextFromBlocks(blocks: Block[]): string {
  return blocks
    .map((block) => {
      switch (block.type) {
        case "header":
          return block.content || "";
        case "text":
          return block.content || "";
        case "list":
          if (block.items && block.items.length > 0) {
            // Enhanced list processing to handle object items
            const processedItems = block.items
              .filter((item: any) => item !== null && item !== undefined)
              .map((item: any) => {
                if (typeof item === "string") {
                  return item;
                } else if (typeof item === "object" && item.content) {
                  return item.content;
                } else if (typeof item === "object" && item.text) {
                  return item.text;
                } else if (typeof item === "object") {
                  return JSON.stringify(item);
                } else {
                  return String(item);
                }
              })
              .map((item: string) => item.replace(/<[^>]*>/g, "").trim())
              .filter(
                (item: string) =>
                  item !== "" && !item.includes("[object Object]")
              );

            return processedItems.join(" ");
          }
          return block.content || "";
        case "image":
          return block.caption || "Image";
        default:
          return block.content || "";
      }
    })
    .join(" ");
}
